import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Switch, FormControlLabel, IconButton, CircularProgress, MenuItem, Dialog, DialogContent, InputAdornment, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { closeModal, toggleLoading } from '../../../state/ModalSlice/ModalSlice';
import { useAppSelector } from '../../../state/state';
import { doc, getDoc, GeoPoint, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../../Config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { RootState, useAppDispatch } from '../../../state/state';
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import AttachmentIcon from '@mui/icons-material/Attachment';
import dayjs from 'dayjs';
import { getGeoHash, getLatLong } from '../utilityFunctions';
import { defaultEventImages } from './AddEventsModal';
import { selectActiveRole, selectBankAccounts, selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2';
import { setFieldsForEditorJSModal } from '../../../state/ArbitrarySlice/ArbitratySlice';
import { openModal } from '../../../state/ModalSlice/ModalSlice';
const GEO_LOC_API = import.meta.env.VITE_GEOLOCATION_API_KEY || process.env.VITE_GEOLOCATION_API_KEY;

interface EditEventsModalProps {

}

enum EventCategory {
    ACTIVITIES = "Activities",
    SPORTS = "Sports",
    CULTURE = "Culture",
    WORKSHOP = "Workshop"
}

interface Ticket {
    ticketDetails: string;
    ticketPrice: string;
    ticketCount: string;
    remainingTicket: string;
}

interface TicketError {
    ticketDetails: boolean;
    ticketPrice: boolean;
    ticketCount: boolean;
}

const EditEventsModal: React.FC<EditEventsModalProps> = () => {
    const dispatch = useAppDispatch();
    const eventId = useAppSelector((state: RootState) => state.ArbitrarySlice.ForEditEventsModal.eventId);
    const isOpen = useAppSelector((state: RootState) => state.ModalSlice.EditEventsModalOpen);
    const providerId: string = useAppSelector(selectEffectiveUID)   

    const [formData, setFormData] = useState({
        eName: '',
        eSummary: '',
        contactPhoneNumber: '',
        isOpen: true,
        eCategory: EventCategory.ACTIVITIES,
        eStartTime: new Date(),
        eEndTime: new Date(),
        paymentProfileId: 0,
        eGeoLocation: { hash: "", latlang: new GeoPoint(0, 0) },
        placeName: "",
        placeAddress: "",
        eTermsConditions: "",
        eRegsStartTime: new Date(),
    });
    const [image, setImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageSizeExceeded, setImageSizeExceeded] = useState(false);
    const [location, setLocation] = useState<any>(null);
    const [whatsappInviteURL, setWhatsappInviteURL] = useState<string>("");
    const [paymentProfileID, setPaymentProfileID] = useState<number>(0);
    const [editorDescriptionContent, setEditorDescriptionContent] = useState<any>(null);
    const [editorTermsContent, setEditorTermsContent] = useState<any>(null);
    const [tickets, setTickets] = useState<Ticket[]>([{
        ticketDetails: '',
        ticketPrice: '',
        ticketCount: '',
        remainingTicket: ''
    }]);
    const [ticketErrors, setTicketErrors] = useState<TicketError[]>([{
        ticketDetails: false,
        ticketPrice: false,
        ticketCount: false
    }]);
    const [errors, setErrors] = useState({
        eName: false,
        eSummary: false,
        eStartTime: false,
        eEndTime: false,
        dateTime: false
    });

    useEffect(() => {
        const fetchEvent = async () => {
            if (eventId && isOpen) {
                try {
                    const eventDoc = await getDoc(doc(db, 'ServiceProviders', providerId, 'Events', eventId));
                    if (eventDoc.exists()) {
                        const eventData = eventDoc.data();
                        setFormData({
                            eName: eventData.eName || '',
                            eSummary: eventData.eSummary || '',
                            contactPhoneNumber: eventData.contactPhoneNumber || '',
                            isOpen: eventData.isOpen ?? true,
                            eCategory: eventData.eCategory || EventCategory.ACTIVITIES,
                            eStartTime: eventData.eStartTime.toDate(),
                            eEndTime: eventData.eEndTime.toDate(),
                            paymentProfileId: eventData.paymentProfileId || 0,
                            eGeoLocation: eventData.eGeoLocation || { hash: "", latlang: new GeoPoint(0, 0) },
                            placeName: eventData.placeName || "",
                            placeAddress: eventData.placeAddress || "",
                            eTermsConditions: eventData.eTermsConditions || "",
                            eRegsStartTime: eventData.eRegsStartTime.toDate(),
                        });

                        // Initialize editor content if it exists in the database
                        if (eventData.editorDescriptionContent) {
                            setEditorDescriptionContent(eventData.editorDescriptionContent);
                        }
                        if (eventData.editorTermsContent) {
                            setEditorTermsContent(eventData.editorTermsContent);
                        }
                        
                        // Initialize whatsappInviteURL if it exists
                        if (eventData.whatsappInviteURL) {
                            setWhatsappInviteURL(eventData.whatsappInviteURL);
                        }

                        if (eventData.tickets) {
                            const newTickets = eventData.tickets.map((ticket: any) => ({
                                ticketDetails: ticket.ticketDetails,
                                ticketPrice: ticket.ticketPrice.toString(),
                                ticketCount: ticket.ticketCount.toString(),
                                remainingTicket: ticket.remainingTicket ? ticket.remainingTicket.toString() : ticket.ticketCount.toString()
                            }));

                            console.log(newTickets);
                            setTickets(newTickets);
                            
                            // Initialize corresponding error states for each ticket
                            setTicketErrors(newTickets.map(() => ({
                                ticketDetails: false,
                                ticketPrice: false,
                                ticketCount: false
                            })));
                        }

                        if (eventData.ePromoImage) {
                            setImagePreview(eventData.ePromoImage);
                        }

                        if (eventData.eGeoLocation) {
                            setLocation({
                                label: eventData.placeAddress,
                                value: {
                                    structured_formatting: {
                                        main_text: eventData.placeName
                                    }
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.error("Error fetching event:", error);
                }
            }
        };

        fetchEvent();
    }, [eventId, isOpen]);

    // Listen for editor save events
    useEffect(() => {
        const handleEditorSave = (event: any) => {
            const { content, fieldType, sourceModal } = event.detail;
            
            // Only update if the event is coming from this modal
            if (sourceModal !== 'EditEventsModal') {
                return;
            }
            
            if (fieldType === 'description') {
                setEditorDescriptionContent(content);
                // Convert content to plain text for form submission
                const plainText = content.blocks.map((block: any) => {
                    if (block.type === 'paragraph' || block.type === 'header') {
                        return block.data.text;
                    }
                    return '';
                }).join('\n');
                setFormData(prev => ({ ...prev, eSummary: plainText }));
            } else if (fieldType === 'terms') {
                setEditorTermsContent(content);
                // Convert content to plain text for form submission
                const plainText = content.blocks.map((block: any) => {
                    if (block.type === 'paragraph' || block.type === 'header') {
                        return block.data.text;
                    }
                    return '';
                }).join('\n');
                setFormData(prev => ({ ...prev, eTermsConditions: plainText }));
            }
        };

        window.addEventListener('editorSaveContent', handleEditorSave);
        
        return () => {
            window.removeEventListener('editorSaveContent', handleEditorSave);
        };
    }, []);

    useEffect(() => {
        if (location) {

            getLatLong(location.value.place_id).then((geometry: { lat: any, lng: any }) => {
                const geoPoint = new GeoPoint(geometry.lat, geometry.lng);

                setFormData(prev => ({
                    ...prev, eGeoLocation: { hash: geoPoint != null ? getGeoHash(geoPoint) || "" : "", latlang: geoPoint },
                    placeName: location.value.structured_formatting.main_text,
                    placeAddress: location.label
                }));
            })
        }
    }, [location])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        dispatch(toggleLoading(true));
        try {
            let imageUrl = imagePreview || '';
            if (image) {
                const storageRef = ref(storage, `events/${providerId}/${image.name}`);
                await uploadBytes(storageRef, image);
                imageUrl = await getDownloadURL(storageRef);
            }

            const eventRef = doc(db, 'ServiceProviders', providerId, 'Events', eventId);

            if (imageUrl === "") {
                imageUrl = defaultEventImages[formData.eCategory as keyof typeof defaultEventImages];
            }

            await updateDoc(eventRef, {
                ...formData,
                tickets: tickets.map(ticket => ({
                    ticketDetails: ticket.ticketDetails,
                    ticketPrice: Number(ticket.ticketPrice),
                    ticketCount: Number(ticket.ticketCount),
                    remainingTicket : ticket.remainingTicket ? Number(ticket.remainingTicket) : Number(ticket.ticketCount)
                    
                })),
                ePromoImage: imageUrl,
                eGeoLocation: {
                    hash: formData.eGeoLocation.hash, latlang: formData.eGeoLocation.latlang, placeName: formData.placeName,
                    address: formData.placeAddress
                },
                placeName: formData.placeName,
                placeAddress: formData.placeAddress,
                accountId: BankAccounts ? BankAccounts[paymentProfileID].razorpayAccountId : null,
                // Save editor content
                editorDescriptionContent: editorDescriptionContent,
                editorTermsContent: editorTermsContent,
                whatsappInviteURL: whatsappInviteURL || ""
            });

            handleClose();
        } catch (error) {
            console.error("Error updating event:", error);
        } finally {
            dispatch(toggleLoading(false));
        }
    };

    const handleClose = () => {
        dispatch(closeModal('EditEventsModalOpen'));
        ResetStates();
    };

    function ResetStates() {
        setFormData({
            eName: '',
            eSummary: '',
            contactPhoneNumber: '',
            isOpen: true,
            eCategory: EventCategory.ACTIVITIES,
            eStartTime: new Date(),
            eEndTime: new Date(),
            paymentProfileId: 0,
            eGeoLocation: { hash: "", latlang: new GeoPoint(0, 0) },
            placeName: "",
            placeAddress: "",
            eTermsConditions: "",
            eRegsStartTime: new Date(),
        });
        setImage(null);
        setImagePreview(null);
        setErrors({
            eName: false,
            eSummary: false,
            eStartTime: false,
            eEndTime: false,
            dateTime: false
        });
        setTickets([{
            ticketDetails: '',
            ticketPrice: '',
            ticketCount: '',
            remainingTicket: ''
        }]);
        setTicketErrors([{
            ticketDetails: false,
            ticketPrice: false,
            ticketCount: false
        }]);
        setLocation(null);
        setWhatsappInviteURL("");
        setEditorDescriptionContent(null);
        setEditorTermsContent(null);
    }



    const handleChangeBuzzName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, eName: event.target.value }));
        setErrors(prev => ({ ...prev, eName: false }));
    };



    const handleChangeCategory = (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
        setFormData(prev => ({ ...prev, eCategory: event.target.value as EventCategory }));
    };

    const handleChangeContactPhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Allow only numbers, spaces, hyphens, parentheses, and plus sign
        const phoneRegex = /^[0-9\s\-()+]*$/;
        if (phoneRegex.test(event.target.value) || event.target.value === '') {
            setFormData(prev => ({ ...prev, contactPhoneNumber: event.target.value }));
        }
    };

    const handleChangeIsLive = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, isOpen: event.target.checked }));
    };



    const handleOpenDescriptionEditor = () => {
        dispatch(setFieldsForEditorJSModal({
            content: editorDescriptionContent,
            fieldType: 'description',
            sourceModal: 'EditEventsModal'
        }));
        dispatch(openModal('EditorJSModalOpen'));
    };

    const handleOpenTermsEditor = () => {
        dispatch(setFieldsForEditorJSModal({
            content: editorTermsContent,
            fieldType: 'terms',
            sourceModal: 'EditEventsModal'
        }));
        dispatch(openModal('EditorJSModalOpen'));
    };

    // Helper function to display editor content preview
    const getContentPreview = (content: any, fallbackText: string) => {
        if (!content || !content.blocks || content.blocks.length === 0) {
            return fallbackText;
        }
        
        return content.blocks.map((block: any) => {
            if (block.type === 'paragraph' && block.data.text) {
                return block.data.text;
            } else if (block.type === 'header' && block.data.text) {
                return block.data.text;
            }
            return '';
        }).filter(text => text).join(' ').substring(0, 150) + (content.blocks.some((block: any) => block.data.text && block.data.text.length > 0) ? '...' : '');
    };

    const handleChangePaymentProfile = (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
        const value = Number(event.target.value);
        setFormData(prev => ({ ...prev, paymentProfileId: value }));
        setPaymentProfileID(value);
    };

    const handleAddTicket = () => {
        setTickets([...tickets, { ticketDetails: '', ticketPrice: '', ticketCount: '', remainingTicket: '' }]);
        setTicketErrors([...ticketErrors, { ticketDetails: false, ticketPrice: false, ticketCount: false }]);
    };

    const handleRemoveTicket = (index: number) => {
        if (tickets.length > 1) {
            const newTickets = tickets.filter((_, i) => i !== index);
            const newErrors = ticketErrors.filter((_, i) => i !== index);
            setTickets(newTickets);
            setTicketErrors(newErrors);
        }
    };

    const handleTicketChange = (index: number, field: keyof Ticket, value: string) => {
        const newTickets = [...tickets];
        const newErrors = [...ticketErrors];

        if (field === 'ticketPrice') {
            if (!value) {
                newTickets[index][field] = '';
            } else {
                let decimalCount = 0;
                let isValid = true;

                for (let i = 0; i < value.length; i++) {
                    const char = value[i];
                    if (char >= '0' && char <= '9') {
                        // Valid number, continue
                    } else if (char === '.') {
                        if (decimalCount === 0) {
                            decimalCount++;
                        } else {
                            isValid = false;
                            break;
                        }
                    } else {
                        isValid = false;
                        break;
                    }
                }

                if (isValid) {
                    newTickets[index][field] = value;
                }
            }
        } else if (field === 'ticketCount') {
            if (!/^\d*$/.test(value)) return;
            newTickets[index][field] = value;
        } else {
            newTickets[index][field] = value;
        }

        newErrors[index] = { ...newErrors[index], [field]: false };
        setTickets(newTickets);
        setTicketErrors(newErrors);
    };

    const handleChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = new Date(formData.eStartTime);
        const [year, month, day] = event.target.value.split('-').map(Number);
        newDate.setFullYear(year, month - 1, day);
        setFormData(prev => ({ ...prev, eStartTime: newDate }));
        setErrors(prev => ({ ...prev, eStartTime: false }));
    };

    const handleChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = new Date(formData.eEndTime);
        const [year, month, day] = event.target.value.split('-').map(Number);
        newDate.setFullYear(year, month - 1, day);
        setFormData(prev => ({ ...prev, eEndTime: newDate }));
        setErrors(prev => ({ ...prev, eEndTime: false }));
    };

    const handleChangeStartTime = (newValue: any) => {
        if (newValue) {
            const newDate = new Date(formData.eStartTime);
            newDate.setHours(newValue.hour(), newValue.minute());
            setFormData(prev => ({ ...prev, eStartTime: newDate }));
        }
    };

    const handleChangeEndTime = (newValue: any) => {
        if (newValue) {
            const newDate = new Date(formData.eEndTime);
            newDate.setHours(newValue.hour(), newValue.minute());
            setFormData(prev => ({ ...prev, eEndTime: newDate }));
        }
    };

    const handleChangeRegsStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = new Date(formData.eRegsStartTime);
        const [year, month, day] = event.target.value.split('-').map(Number);
        newDate.setFullYear(year, month - 1, day);
        setFormData(prev => ({ ...prev, eRegsStartTime: newDate }));
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = { ...errors };
        const newTicketErrors = tickets.map(ticket => ({
            ticketDetails: !ticket.ticketDetails.trim(),
            ticketPrice: !ticket.ticketPrice.trim() || parseFloat(ticket.ticketPrice) < 6,
            ticketCount: !ticket.ticketCount.trim() || Number(ticket.ticketCount) <= 0
        }));

        if (formData.eName.trim() === '') {
            newErrors.eName = true;
            isValid = false;
        }
        if (formData.eSummary.trim() === '') {
            newErrors.eSummary = true;
            isValid = false;
        }
        if (!formData.eStartTime) {
            newErrors.eStartTime = true;
            isValid = false;
        }
        if (!formData.eEndTime) {
            newErrors.eEndTime = true;
            isValid = false;
        }
        if (formData.eStartTime >= formData.eEndTime) {
            newErrors.dateTime = true;
            isValid = false;
        }

        if (newTicketErrors.some(error => error.ticketDetails || error.ticketPrice || error.ticketCount)) {
            isValid = false;
        }

        setErrors(newErrors);
        setTicketErrors(newTicketErrors);
        return isValid;
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        setImageSizeExceeded(false);
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setImageSizeExceeded(true);
                return;
            }
            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const BankAccounts = useAppSelector(selectBankAccounts);
    const bankAccountOptions = BankAccounts ? BankAccounts.map(bankAccount =>
        bankAccount.iFSC.slice(0, 4) + " - " + bankAccount.accountNo
    ) : [];
    const activeRole = useAppSelector(selectActiveRole);
    const isManager = activeRole?.role === 'Manager';

    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);

    return (
        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                    width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '600px', md: '800px' },
                    minWidth: { sm: '600px', md: '800px' },
                    margin: { xs: '16px', sm: 'auto' },
                    maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' },
                    overflow: 'hidden',
                    transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms !important'
                },
            }}
            maxWidth="md"
            fullWidth
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ 
                paddingX: { xs: "16px", sm: "24px", md: "60px" }, 
                paddingY: { xs: "30px", sm: "40px", md: "50px" },
                maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' },
                minHeight: { sm: '70vh' },
                overflowY: 'auto'
            }} className={"bg-white"}>
                <div className={"w-full max-w-full"}>
                    <div className={"header pb-3 sm:pb-[20px]"}>
                        <p className={"text-lg sm:text-cf_heading font-[600] "}>Edit Event</p>
                    </div>

                    <div className={"absolute right-2 sm:right-4 top-2 sm:top-4"}>
                        <IconButton onClick={handleClose} size="small" sx={{ padding: { xs: '4px', sm: '8px' } }}>
                            <CloseIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                        </IconButton>
                    </div>

                    <div className={"pt-0"}>
                        <div className={""}>
                            <div className={""}>
                                <Box
                                    component={"div"}
                                    sx={{}}
                                >
                                    <div className="flex flex-col gap-4">
                                        <TextField
                                            label="Event Name*"
                                            value={formData.eName}
                                            onChange={handleChangeBuzzName}
                                            error={errors.eName}
                                            helperText={errors.eName ? "Buzz Name is required" : ""}
                                            fullWidth
                                            size='small'
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: { xs: '100%', sm: '550px' },
                                            }}
                                        />


                                        <div className="w-full">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Event Description*
                                            </label>
                                            <div
                                                onClick={handleOpenDescriptionEditor}
                                                className={`w-full min-h-[80px] p-3 border rounded-lg cursor-pointer hover:border-blue-500 transition-colors ${
                                                    errors.eSummary ? 'border-red-500' : 'border-gray-300'
                                                } ${editorDescriptionContent ? 'text-gray-900' : 'text-gray-500'}`}
                                                style={{ fontSize: '14px', maxWidth: '550px' }}
                                            >
                                                {getContentPreview(editorDescriptionContent, formData.eSummary || 'Click to edit event description with rich text editor...')}
                                            </div>
                                            {errors.eSummary && (
                                                <p className="text-red-500 text-xs mt-1">Description is required</p>
                                            )}
                                        </div>
                                        <TextField
                                            select
                                            label="Category"
                                            value={formData.eCategory}
                                            onChange={handleChangeCategory}
                                            fullWidth
                                            size='small'
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: { xs: '100%', sm: '550px' },
                                            }}
                                        >
                                            {Object.values(EventCategory).map((category) => (
                                                <MenuItem key={category} value={category}>
                                                    {category}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <Tooltip title="This phone number will be shown to users when they try to buy tickets" placement="top">
                                            <TextField
                                                label="Contact Phone Number"
                                                value={formData.contactPhoneNumber}
                                                onChange={handleChangeContactPhoneNumber}
                                                placeholder="e.g., +91 98765 43210"
                                                fullWidth
                                                size='small'
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">📞</InputAdornment>,
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: 'grey',
                                                            borderRadius: '10px',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'grey',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'skyblue',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                    width: { xs: '100%', sm: '550px' },
                                                }}
                                            />
                                        </Tooltip>

                                        <TextField
                                            label="WhatsApp Invite URL (Optional)"
                                            value={whatsappInviteURL}
                                            onChange={(e) => setWhatsappInviteURL(e.target.value)}
                                            placeholder="https://chat.whatsapp.com/..."
                                            size='small'
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: { xs: '100%', sm: '550px' },
                                            }}
                                        />

                                        <div className="flex flex-col gap-1 max-w-[550px]">
                                            <p className="text-xs sm:text-sm text-gray-600 font-medium">Location</p>
                                            <GooglePlacesAutocomplete
                                                apiKey={GEO_LOC_API || process.env.GEO_LOC_API}
                                                selectProps={{
                                                    placeholder: "Search location",
                                                    onChange: setLocation,
                                                    components: {
                                                        DropdownIndicator: () => (
                                                            <div className="px-2">
                                                                <LocationOnIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} style={{ color: '#6B7280' }} />
                                                            </div>
                                                        )
                                                    },
                                                    styles: {
                                                        input: (provided) => ({
                                                            ...provided, backgroundColor: 'transparent', // Fully opaque white background
                                                            fontSize: '14px'
                                                        }), option: (provided) => ({
                                                            ...provided, backgroundColor: 'transparent', // Fully opaque white background
                                                            fontSize: '14px'
                                                        }), singleValue: (provided) => ({
                                                            ...provided, backgroundColor: 'transparent', // Fully opaque white background
                                                            fontSize: '14px'
                                                        }), control: (provided) => ({
                                                            ...provided,
                                                            backgroundColor: 'transparent', // Fully opaque white background
                                                            borderColor: "gray",
                                                            boxShadow: 'none', // Remove the default shadow
                                                            '&:hover': {
                                                                borderColor: 'black', // Keep the border black on hover
                                                            },
                                                            '&:focus': {
                                                                borderColor: 'black', // Ensure the border is black when active
                                                            },
                                                            '&:active': {
                                                                borderColor: "black"
                                                            },
                                                            borderRadius: '10px',
                                                            fontSize: '14px'
                                                        }), menu: (provided) => ({
                                                            ...provided, backgroundColor: 'rgba(255, 255, 255, 1)', // Fully opaque white background
                                                            zIndex: 100, // Set z-index to 100
                                                        }), menuPortal: (provided) => ({
                                                            ...provided, zIndex: 100, // Ensure the menu portal has a z-index of 100
                                                        }),
                                                    },
                                                }}
                                            />
                                            {location && (
                                                <p className="text-xs sm:text-sm text-gray-500 mt-1 break-words">
                                                    Selected: {location.label}
                                                </p>
                                            )}
                                        </div>

                                        {/* Add Tickets Section */}
                                        <div className="">
                                            <div className="flex items-center justify-between mb-3 sm:mb-4">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-xs sm:text-sm text-gray-600 font-medium">Add Tickets</h3>
                                                    <Tooltip title="Add different ticket types with their respective prices and maximum quantities">
                                                        <InfoIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} className="text-gray-500" />
                                                    </Tooltip>
                                                </div>
                                            </div>

                                            {tickets.map((ticket, index) => (
                                                <div key={index} className="flex flex-col sm:flex-row items-start gap-2 sm:gap-4 mb-3 sm:mb-4">
                                                    <TextField
                                                        label="Ticket Name"
                                                        value={ticket.ticketDetails}
                                                        onChange={(e) => handleTicketChange(index, 'ticketDetails', e.target.value)}
                                                        error={ticketErrors[index].ticketDetails}
                                                        helperText={ticketErrors[index].ticketDetails ? "Required" : ""}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px'
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: { xs: '14px', sm: '14px' },
                                                                },
                                                            },
                                                            width: { xs: '100%', sm: '250px' }
                                                        }}
                                                    />
                                                    <TextField
                                                        label="Price"
                                                        value={ticket.ticketPrice}
                                                        onChange={(e) => handleTicketChange(index, 'ticketPrice', e.target.value)}
                                                        error={ticketErrors[index].ticketPrice}
                                                        helperText={ticketErrors[index].ticketPrice ? "Minimum ₹6 required" : ""}
                                                        size="small"
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start" sx={{ fontSize: { xs: '14px', sm: '14px' } }}>₹</InputAdornment>,
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px'
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: { xs: '14px', sm: '14px' },
                                                                },
                                                            },
                                                            width: { xs: '100%', sm: '150px' }
                                                        }}
                                                    />
                                                    <TextField
                                                        label="Max Seats"
                                                        value={ticket.ticketCount}
                                                        onChange={(e) => handleTicketChange(index, 'ticketCount', e.target.value)}
                                                        error={ticketErrors[index].ticketCount}
                                                        helperText={ticketErrors[index].ticketCount ? "Required" : ""}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px'
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: { xs: '14px', sm: '14px' },
                                                                },
                                                            },
                                                            width: { xs: '100%', sm: '100px' }
                                                        }}
                                                    />
                                                    <div className="flex items-center gap-1 sm:gap-2">
                                                        {index === tickets.length - 1 && (
                                                            <IconButton
                                                                onClick={handleAddTicket}
                                                                size="small"
                                                                sx={{ padding: { xs: '4px', sm: '8px' } }}
                                                            >
                                                                <AddIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                                            </IconButton>
                                                        )}
                                                        {tickets.length > 1 && (
                                                            <IconButton
                                                                onClick={() => handleRemoveTicket(index)}
                                                                size="small"
                                                                sx={{ padding: { xs: '4px', sm: '8px' } }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                                            </IconButton>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>


                                        <div className="flex flex-col gap-3 sm:gap-4">
                                            <div>
                                                <p className="text-xs sm:text-sm text-gray-600 font-medium mb-2">Start Date & Time</p>
                                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                                                    <TextField
                                                        type="date"
                                                        value={formData.eStartTime.toISOString().split('T')[0]}
                                                        onChange={handleChangeStartDate}
                                                        error={errors.eStartTime}
                                                        helperText={errors.eStartTime ? "Start Date is required" : ""}
                                                        size='small'
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px',
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: { xs: '14px', sm: '14px' },
                                                                },
                                                            },
                                                            width: { xs: '100%', sm: '260px' }
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <TimePicker
                                                            value={dayjs(formData.eStartTime)}
                                                            onChange={handleChangeStartTime}
                                                            viewRenderers={{
                                                                hours: renderTimeViewClock,
                                                                minutes: renderTimeViewClock,
                                                            }}
                                                            ampm={true}
                                                            slotProps={{
                                                                textField: {
                                                                    size: 'small',
                                                                    sx: {
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': {
                                                                                borderColor: 'grey',
                                                                                borderRadius: '10px',
                                                                            },
                                                                            '&:hover fieldset': {
                                                                                borderColor: 'grey',
                                                                            },
                                                                            '&.Mui-focused fieldset': {
                                                                                borderColor: 'skyblue',
                                                                            },
                                                                            '& .MuiInputBase-input': {
                                                                                fontSize: { xs: '14px', sm: '14px' },
                                                                            },
                                                                        },
                                                                        width: { xs: '100%', sm: '260px' }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-xs sm:text-sm text-gray-600 font-medium mb-2">End Date & Time</p>
                                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                                                    <TextField
                                                        type="date"
                                                        value={formData.eEndTime.toISOString().split('T')[0]}
                                                        onChange={handleChangeEndDate}
                                                        error={errors.eEndTime}
                                                        helperText={errors.eEndTime ? "End Date is required" : ""}
                                                        size='small'
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px',
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue',
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: { xs: '14px', sm: '14px' },
                                                                },
                                                            },
                                                            width: { xs: '100%', sm: '260px' }
                                                        }}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <TimePicker
                                                            value={dayjs(formData.eEndTime)}
                                                            onChange={handleChangeEndTime}
                                                            viewRenderers={{
                                                                hours: renderTimeViewClock,
                                                                minutes: renderTimeViewClock,
                                                            }}
                                                            ampm={true}
                                                            slotProps={{
                                                                textField: {
                                                                    size: 'small',
                                                                    sx: {
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': {
                                                                                borderColor: 'grey',
                                                                                borderRadius: '10px',
                                                                            },
                                                                            '&:hover fieldset': {
                                                                                borderColor: 'grey',
                                                                            },
                                                                            '&.Mui-focused fieldset': {
                                                                                borderColor: 'skyblue',
                                                                            },
                                                                            '& .MuiInputBase-input': {
                                                                                fontSize: { xs: '14px', sm: '14px' },
                                                                            },
                                                                        },
                                                                        width: { xs: '100%', sm: '260px' }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                            </div>
                                            {errors.dateTime && (
                                                <p className="text-red-500 text-xs sm:text-sm">End date and time cannot be earlier than start date and time</p>
                                            )}
                                        </div>


                                        <div>
                                            <p className="text-xs sm:text-sm text-gray-600 font-medium mb-2">Registration Start</p>
                                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-stretch sm:items-center">
                                                <TextField
                                                    type="date"
                                                    value={formData.eRegsStartTime.toISOString().split('T')[0]}
                                                    onChange={handleChangeRegsStartDate}
                                                    size='small'
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: 'grey',
                                                                borderRadius: '10px',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'grey',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'skyblue',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: { xs: '14px', sm: '14px' },
                                                            },
                                                        },
                                                        width: { xs: '100%', sm: '260px' }
                                                    }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={formData.isOpen}
                                                            onChange={handleChangeIsLive}
                                                            sx={{ padding: { xs: '4px', sm: '9px' } }}
                                                        />
                                                    }
                                                    label={<span className="text-xs sm:text-sm">Is Registration Open?</span>}
                                                />
                                            </div>
                                        </div>

                                        <div className="max-w-[550px]">
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                fullWidth
                                                sx={{
                                                    mb: 0,
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    color: '#1D4ED8',
                                                    borderColor: '#1D4ED8',
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    py: { xs: 0.75, sm: 1 },
                                                    '&:hover': {
                                                        backgroundColor: '#1D4ED8', color: "#FFFFFF"
                                                    },
                                                }}
                                            >
                                                <span className={"mr-1 flex justify-center items-center max-w-[550px]"}><AttachmentIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} /> </span> <span className="hidden sm:inline">Click here to upload Events Display Image</span><span className="sm:hidden">Upload Image</span>
                                                <input
                                                    type="file"
                                                    accept="image/jpeg, image/png"
                                                    hidden
                                                    onChange={handleImageChange}
                                                />
                                            </Button>
                                        </div>
                                        {imageSizeExceeded && (
                                            <p className="text-xs sm:text-sm" style={{ color: 'red' }}>Image size should not exceed 5MB</p>
                                        )}
                                        {imagePreview && (
                                            <Box sx={{ mb: 2, position: 'relative', width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 } }}>
                                                <img src={imagePreview} alt="Logo preview"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                                                <IconButton
                                                    onClick={() => {
                                                        setImage(null);
                                                        setImagePreview(null);
                                                    }}
                                                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', padding: { xs: '4px', sm: '8px' } }}
                                                >
                                                    <CloseIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                                </IconButton>
                                            </Box>
                                        )}

                                        <TextField
                                            size={"small"}
                                            id="Bank Account"
                                            select
                                            label="Bank account"
                                            value={paymentProfileID}
                                            onChange={handleChangePaymentProfile}
                                            disabled={isManager}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: { xs: '100%', sm: '550px' },
                                            }}
                                        >
                                            {/* Assuming you have a way to get bank account options */}
                                            {bankAccountOptions.length > 0 ? bankAccountOptions.map((option, index) => (
                                                <MenuItem key={index} value={index} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>
                                                    {option}
                                                </MenuItem>
                                            )) : <MenuItem key="0" value={0} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>No account Found</MenuItem>}
                                        </TextField>

                                        <div className="w-full max-w-[550px]">
                                            <label className="block text-sm font-medium text-gray-700 mb-2 max-w-[550px]">
                                                Terms and Conditions (Optional)
                                            </label>
                                            <div
                                                onClick={handleOpenTermsEditor}
                                                className={`w-full min-h-[100px] p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors ${
                                                    editorTermsContent ? 'text-gray-900' : 'text-gray-500'
                                                }`}
                                                style={{ fontSize: '14px' }}
                                            >
                                                {getContentPreview(editorTermsContent, formData.eTermsConditions || 'Click to edit terms and conditions with rich text editor...')}
                                            </div>
                                        </div>

                                        <div className={"flex flex-col sm:flex-row gap-2 sm:gap-2 pt-4 max-w-[550px]"}>
                                            <button
                                                disabled={isLoading}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClose();
                                                }}
                                                className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 px-3 sm:px-4 py-2 sm:py-1.5 text-sm sm:text-base rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSubmit}
                                                disabled={isLoading}
                                                className={`w-full bg-cc_brand text-white px-3 sm:px-4 py-2 sm:py-1.5 text-sm sm:text-base rounded-lg border-gray-500 flex justify-center items-center gap-2 ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                                            >
                                                {isLoading && <CircularProgress sx={{ color: "white" }} size={16} />}
                                                <span>{isLoading ? "Loading" : "Confirm"}</span>
                                            </button>
                                        </div>
                                    </div>
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>


            </DialogContent>
        </Dialog>
    );
};

export default EditEventsModal;
